import { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import { AsyncCreatableSelect } from '../../../components/select-form/select-form';
import { components } from 'react-select';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import classNames from 'classnames';
import { UserRepresentativeForm } from './user-representative-form';
import { formatPhoneNumber } from '../../../util/util';
import { useRepresentativeApi } from '../../../clients/quipli-service/representatives/representative-api';
import { Representative } from '../../../clients/quipli-service/representatives/representative';

export const RepresentativeSelect = ({
  userId,
  setRepresentative,
  isDisabled = false,
}: {
  userId: number;
  setRepresentative: (r?: Representative) => void;
  isDisabled?: boolean;
}) => {
  const { searchRepresentatives } = useRepresentativeApi();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Callbacks
  const search = useCallback(
    debounce((keyword: string, callback) => {
      searchRepresentatives(userId, {
        keyword,
        pageNumber: 1,
        pageSize: 30,
      }).then((res) => callback(res.items));
    }, 300),
    [],
  );

  return (
    <>
      <AsyncCreatableSelect<Representative>
        cacheOptions
        defaultOptions
        isDisabled={isDisabled}
        loadOptions={search}
        onClickCreatableOption={() => setIsModalOpen(true)}
        getOptionLabel={(op) => op.name}
        getOptionValue={(op) => `${op.id}`}
        onChange={(value) => {
          setRepresentative(value ?? undefined);
        }}
        loadingMessage={() => 'Loading representatives...'}
        noOptionsMessage={() => 'No representatives found'}
        components={{
          Placeholder: (props) => (
            <components.Placeholder {...props}>
              <Typography type="text-sm">Search Representatives</Typography>
            </components.Placeholder>
          ),
          ValueContainer: (props) => (
            <div className="flex items-center gap-[6px] pl-[10px]">
              <Icon icon="Search" size="medium" stroke="var(--grey-500)" />
              <components.ValueContainer {...props} />
            </div>
          ),
          Option: (props) => {
            //@ts-expect-error __isNew__
            const isCreateOption = props.data.__isNew__ === true;

            return (
              <components.Option {...props}>
                <div
                  className={classNames('flex items-center gap-[6px]', {
                    'justify-between': !isCreateOption,
                  })}
                >
                  {isCreateOption ? (
                    <>
                      <div className="quipli-v2 flex items-center">
                        <Icon icon="Plus" stroke="var(--quipli-black-800)" />
                      </div>
                      <Typography type="text-md" weight="medium" className="text-grey-900">
                        Add new representative
                      </Typography>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-[4px]">
                        <Typography type="text-md" weight="medium" className="text-grey-900">
                          {props.children}
                        </Typography>
                        <div>
                          {props.data.role && (
                            <Typography type="text-sm" className="text-grey-500">
                              {props.data.role.name}
                            </Typography>
                          )}
                          <Typography type="text-sm" className="text-grey-500">
                            {formatPhoneNumber(props.data.phone)}
                          </Typography>
                          <Typography type="text-sm" className="text-grey-500">
                            {props.data.email}
                          </Typography>
                        </div>
                      </div>
                      {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
                    </>
                  )}
                </div>
              </components.Option>
            );
          },
        }}
      />
      <UserRepresentativeForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
        setRepresentative={setRepresentative}
      />
    </>
  );
};
